"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CosmosSdkBaseAdapter = void 0;
const types_1 = require("@shapeshiftoss/types");
const bech32_1 = require("bech32");
const ErrorHandler_1 = require("../error/ErrorHandler");
const utils_1 = require("../utils");
const CHAIN_TO_BECH32_PREFIX_MAPPING = {
    [types_1.ChainTypes.Cosmos]: 'cosmos',
    [types_1.ChainTypes.Osmosis]: 'osmo'
};
const transformValidator = (validator) => ({
    address: validator.address,
    moniker: validator.moniker,
    tokens: validator.tokens,
    commission: validator.commission.rate,
    apr: validator.apr
});
class CosmosSdkBaseAdapter {
    constructor(args) {
        if (args.chainId && this.supportedChainIds.includes(args.chainId)) {
            this.chainId = args.chainId;
        }
        CosmosSdkBaseAdapter.defaultBIP44Params = (this.constructor).defaultBIP44Params;
        this.providers = args.providers;
    }
    getChainId() {
        return this.chainId;
    }
    async getAccount(pubkey) {
        try {
            const { data } = await this.providers.http.getAccount({ pubkey });
            const delegations = data.delegations.map((delegation) => ({
                assetId: this.assetId,
                amount: delegation.balance.amount,
                validator: transformValidator(delegation.validator)
            }));
            const redelegations = data.redelegations.map((redelegation) => ({
                destinationValidator: transformValidator(redelegation.destinationValidator),
                sourceValidator: transformValidator(redelegation.sourceValidator),
                entries: redelegation.entries.map((entry) => ({
                    assetId: this.assetId,
                    completionTime: Number(entry.completionTime),
                    amount: entry.balance
                }))
            }));
            const undelegations = data.unbondings.map((undelegation) => ({
                validator: transformValidator(undelegation.validator),
                entries: undelegation.entries.map((entry) => ({
                    assetId: this.assetId,
                    completionTime: Number(entry.completionTime),
                    amount: entry.balance.amount
                }))
            }));
            const rewards = data.rewards.map((validatorReward) => ({
                validator: transformValidator(validatorReward.validator),
                rewards: validatorReward.rewards.map((reward) => ({
                    assetId: this.assetId,
                    amount: reward.amount
                }))
            }));
            return {
                balance: data.balance,
                chainId: this.chainId,
                assetId: this.assetId,
                chain: this.getType(),
                chainSpecific: {
                    accountNumber: data.accountNumber.toString(),
                    delegations,
                    redelegations,
                    undelegations,
                    rewards,
                    sequence: data.sequence.toString()
                },
                pubkey: data.pubkey
                /* TypeScript can't guarantee the correct type for the chainSpecific field because of the generic return type.
                   It is preferable to define and type the return instead of applying the cast below, but that's left as an exercise
                   for the reader. */
            };
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    buildBIP44Params(params) {
        return { ...CosmosSdkBaseAdapter.defaultBIP44Params, ...params };
    }
    async getTxHistory(input) {
        try {
            const { data } = await this.providers.http.getTxHistory({
                pubkey: input.pubkey,
                pageSize: input.pageSize,
                cursor: input.cursor
            });
            const txs = await Promise.all(data.txs.map(async (tx) => {
                const parsedTx = await this.parser.parse(tx, input.pubkey);
                return {
                    address: input.pubkey,
                    blockHash: parsedTx.blockHash,
                    blockHeight: parsedTx.blockHeight,
                    blockTime: parsedTx.blockTime,
                    chainId: parsedTx.chainId,
                    chain: this.getType(),
                    confirmations: parsedTx.confirmations,
                    txid: parsedTx.txid,
                    fee: parsedTx.fee,
                    status: (0, utils_1.getStatus)(parsedTx.status),
                    tradeDetails: parsedTx.trade,
                    transfers: parsedTx.transfers.map((transfer) => ({
                        assetId: transfer.assetId,
                        from: transfer.from,
                        to: transfer.to,
                        type: (0, utils_1.getType)(transfer.type),
                        value: transfer.totalValue
                    })),
                    data: parsedTx.data
                };
            }));
            return {
                cursor: data.cursor,
                pubkey: input.pubkey,
                transactions: txs
            };
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async broadcastTransaction(hex) {
        try {
            const { data } = await this.providers.http.sendTx({ body: { rawTx: hex } });
            return data;
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async validateAddress(address) {
        const chain = this.getType();
        try {
            const { prefix } = bech32_1.bech32.decode(address);
            if (CHAIN_TO_BECH32_PREFIX_MAPPING[chain] !== prefix) {
                throw new Error(`Invalid address ${address} for ChainType: ${chain}`);
            }
            return {
                valid: true,
                result: types_1.chainAdapters.ValidAddressResultType.Valid
            };
        }
        catch (err) {
            console.error(err);
            return { valid: false, result: types_1.chainAdapters.ValidAddressResultType.Invalid };
        }
    }
    async subscribeTxs(input, onMessage, onError) {
        const { wallet, bip44Params = CosmosSdkBaseAdapter.defaultBIP44Params } = input;
        const address = await this.getAddress({ wallet, bip44Params });
        const subscriptionId = (0, utils_1.toRootDerivationPath)(bip44Params);
        await this.providers.ws.subscribeTxs(subscriptionId, { topic: 'txs', addresses: [address] }, async (msg) => {
            const tx = await this.parser.parse(msg.data, msg.address);
            onMessage({
                address: tx.address,
                blockHash: tx.blockHash,
                blockHeight: tx.blockHeight,
                blockTime: tx.blockTime,
                chainId: tx.chainId,
                chain: this.getType(),
                confirmations: tx.confirmations,
                fee: tx.fee,
                status: (0, utils_1.getStatus)(tx.status),
                tradeDetails: tx.trade,
                transfers: tx.transfers.map((transfer) => ({
                    assetId: transfer.assetId,
                    from: transfer.from,
                    to: transfer.to,
                    type: (0, utils_1.getType)(transfer.type),
                    value: transfer.totalValue
                })),
                txid: tx.txid
            });
        }, (err) => onError({ message: err.message }));
    }
    unsubscribeTxs(input) {
        if (!input)
            return this.providers.ws.unsubscribeTxs();
        const { bip44Params = CosmosSdkBaseAdapter.defaultBIP44Params } = input;
        const subscriptionId = (0, utils_1.toRootDerivationPath)(bip44Params);
        this.providers.ws.unsubscribeTxs(subscriptionId, { topic: 'txs', addresses: [] });
    }
    closeTxs() {
        this.providers.ws.close('txs');
    }
    async getValidators() {
        try {
            const { data } = await this.providers.http.getValidators();
            return data.validators.map((validator) => transformValidator(validator));
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async getValidator(address) {
        try {
            const { data: validator } = await this.providers.http.getValidator({ pubkey: address });
            return transformValidator(validator);
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
}
exports.CosmosSdkBaseAdapter = CosmosSdkBaseAdapter;
